import { ILaya } from "../../ILaya";
import { Laya } from "../../Laya";
import { LayaEnv } from "../../LayaEnv";
import { BaseRenderNode2D } from "../NodeRender2D/BaseRenderNode2D";
import { SoundManager } from "../media/SoundManager";
import { Loader } from "../net/Loader";
import { Handler } from "../utils/Handler";
import { Event } from "../events/Event";
import { LayaGL } from "../layagl/LayaGL";
import { SpineShaderInit } from "./material/SpineShaderInit";
import { Vector2 } from "../maths/Vector2";
import { ClassUtils } from "../utils/ClassUtils";
import { SpineNormalRender } from "./optimize/SpineNormalRender";
import { SketonOptimise } from "./optimize/SketonOptimise";
import { SpineEmptyRender } from "./optimize/SpineEmptyRender";
import { Vector3 } from "../maths/Vector3";
import { Vector4 } from "../maths/Vector4";
import { Matrix4x4 } from "../maths/Matrix4x4";
import { Color } from "../maths/Color";
import { ShaderDefines2D } from "../webgl/shader/d2/ShaderDefines2D";
import { SpineOptimizeRender } from "./optimize/SpineOptimizeRender";
export class Spine2DRenderNode extends BaseRenderNode2D {
    static createRenderElement2D() {
        if (this._pool.length > 0) {
            return this._pool.pop();
        }
        let element = LayaGL.render2DRenderPassFactory.createRenderElement2D();
        element.renderStateIsBySprite = false;
        element.nodeCommonMap = ["spine2D"];
        return element;
    }
    static recoverRenderElement2D(value) {
        if (!value.canotPool) {
            this._pool.push(value);
        }
    }
    constructor() {
        super();
        this._currentPlayTime = 0;
        this._pause = true;
        this._playbackRate = 1.0;
        this._playAudio = true;
        this._soundChannelArr = [];
        this.trackIndex = 0;
        this._skinName = "default";
        this._loop = true;
        this._nMatrix_0 = new Vector3;
        this._nMatrix_1 = new Vector3;
        this.physicsUpdate = 2;
        this._useFastRender = true;
        this._needUpdate = false;
        this._renderElements = [];
        this._materials = [];
        this.spineItem = SpineEmptyRender.instance;
        this._spriteShaderData.addDefine(BaseRenderNode2D.SHADERDEFINE_BASERENDER2D);
        this._spriteShaderData.addDefine(SpineShaderInit.SPINE_UV);
        this._spriteShaderData.addDefine(SpineShaderInit.SPINE_COLOR);
    }
    _getcommonUniformMap() {
        return ["BaseRender2D", "Spine2D"];
    }
    get externalSkins() {
        return this._externalSkins;
    }
    set externalSkins(value) {
        if (value) {
            for (let i = value.length - 1; i >= 0; i--) {
                value[i].target = this;
            }
        }
        this._externalSkins = value;
    }
    addCMDCall(context, px, py) {
        let shaderData = this._spriteShaderData;
        let mat = context._curMat;
        this._nMatrix_0.setValue(mat.a, mat.b, mat.tx + mat.a * px + mat.c * py);
        this._nMatrix_1.setValue(mat.c, mat.d, mat.ty + mat.b * px + mat.d * py);
        shaderData.setVector3(BaseRenderNode2D.NMATRIX_0, this._nMatrix_0);
        shaderData.setVector3(BaseRenderNode2D.NMATRIX_1, this._nMatrix_1);
        Vector2.TEMP.setValue(context.width, context.height);
        shaderData.setVector2(BaseRenderNode2D.BASERENDERSIZE, Vector2.TEMP);
        if (this._renderAlpha !== context.globalAlpha) {
            let scolor = this.spineItem.getSpineColor();
            let a = scolor.a * context.globalAlpha;
            let color = shaderData.getColor(BaseRenderNode2D.BASERENDER2DCOLOR) || new Color();
            color.setValue(scolor.r, scolor.g, scolor.b, a);
            shaderData.setColor(BaseRenderNode2D.BASERENDER2DCOLOR, color);
            this._renderAlpha = context.globalAlpha;
        }
        let filter = context._colorFiler;
        if (filter) {
            this._spriteShaderData.addDefine(ShaderDefines2D.FILTERCOLOR);
            Matrix4x4.TEMP.cloneByArray(filter._mat);
            shaderData.setMatrix4x4(ShaderDefines2D.UNIFORM_COLORMAT, Matrix4x4.TEMP);
            Vector4.TEMP.setValue(filter._alpha[0], filter._alpha[1], filter._alpha[2], filter._alpha[3]);
            shaderData.setVector(ShaderDefines2D.UNIFORM_COLORALPHA, Vector4.TEMP);
        }
        else {
            this._spriteShaderData.removeDefine(ShaderDefines2D.FILTERCOLOR);
        }
        context._copyClipInfoToShaderData(shaderData);
        this._lightReceive && this._updateLight();
    }
    resetExternalSkin() {
        if (this._skeleton) {
            this._skeleton = new spine.Skeleton(this._templet.skeletonData);
            this.spineItem.changeSkeleton(this._skeleton);
            this._flushExtSkin();
        }
    }
    get source() {
        return this._source;
    }
    set source(value) {
        this._source = value;
        if (value) {
            let template = ILaya.loader.getRes(value, Loader.SPINE);
            if (template) {
                this.templet = template;
            }
            else {
                ILaya.loader.load(value, Loader.SPINE).then((templet) => {
                    if (!this._source || templet && !templet.isCreateFromURL(this._source))
                        return;
                    if (this.destroyed)
                        return;
                    this.templet = templet;
                });
            }
        }
        else
            this.templet = null;
    }
    get skinName() {
        return this._skinName;
    }
    set skinName(value) {
        this._skinName = value;
        if (this._templet)
            this.showSkinByName(value);
    }
    get animationName() {
        return this._animationName;
    }
    set animationName(value) {
        this._animationName = value;
        if (this._templet)
            this.play(value, this._loop, true);
    }
    get maxDetlaTime() {
        return this._timeKeeper.maxDelta;
    }
    set maxDetlaTime(value) {
        this._timeKeeper.maxDelta = value;
    }
    get loop() {
        return this._loop;
    }
    set loop(value) {
        this._loop = value;
        if (this._templet)
            this.play(this._animationName, this._loop, true);
    }
    set url(value) {
        if (this._skin != value) {
            this._skin = value;
            Laya.loader.load(value, Loader.SPINE).then((templet) => {
                this.init(templet);
            });
        }
    }
    get url() {
        return this._skin;
    }
    get twoColorTint() {
        return this._spriteShaderData.hasDefine(SpineShaderInit.SPINE_TWOCOLORTINT);
    }
    set twoColorTint(value) {
        if (value) {
            this._spriteShaderData.addDefine(SpineShaderInit.SPINE_TWOCOLORTINT);
        }
        else {
            this._spriteShaderData.removeDefine(SpineShaderInit.SPINE_TWOCOLORTINT);
        }
    }
    get templet() {
        return this._templet;
    }
    set templet(value) {
        this.init(value);
    }
    set currentTime(value) {
        if (!this._templet)
            return;
        value /= 1000;
        if (value < this._playStart || (!!this._playEnd && value > this._playEnd) || value > this._duration)
            throw new Error("AnimationPlayer: value must large than playStartTime,small than playEndTime.");
        this._state.update(value - this._currentPlayTime);
        this._currentPlayTime = value;
    }
    get playState() {
        if (this._pause)
            if (this._currentPlayTime)
                return Spine2DRenderNode.PAUSED;
            else
                return Spine2DRenderNode.STOPPED;
        return Spine2DRenderNode.PLAYING;
    }
    set useFastRender(value) {
        if (this._useFastRender === value)
            return;
        this._useFastRender = value;
        if (!this._templet)
            return;
        if (value) {
            this.changeFast();
        }
        else {
            this.changeNormal();
        }
        this.play(this._animationName, this._loop, true, this._currentPlayTime);
    }
    get useFastRender() {
        return this._useFastRender;
    }
    onAwake() {
        if (this._skeleton) {
            if (LayaEnv.isPlaying && this._animationName !== undefined)
                this.play(this._animationName, this._loop, true);
        }
    }
    onEnable() {
        this.owner.on(Event.TRANSFORM_CHANGED, this, this.onTransformChanged);
    }
    onDisable() {
        this.owner.off(Event.TRANSFORM_CHANGED, this, this.onTransformChanged);
    }
    init(templet) {
        if (this.destroyed)
            return;
        if (this._templet) {
            this.clear();
            this.reset();
        }
        this._templet = templet;
        if (!this._templet)
            return;
        this._templet._addReference();
        this._skeleton = new spine.Skeleton(this._templet.skeletonData);
        this._stateData = new spine.AnimationStateData(this._skeleton.data);
        this._state = new spine.AnimationState(this._stateData);
        this._timeKeeper = new TimeKeeper(Laya.timer);
        if (!this._useFastRender) {
            let before = SketonOptimise.normalRenderSwitch;
            SketonOptimise.normalRenderSwitch = true;
            this.spineItem = this._templet.sketonOptimise._initSpineRender(this._skeleton, this._templet, this, this._state);
            SketonOptimise.normalRenderSwitch = before;
        }
        else
            this.spineItem = this._templet.sketonOptimise._initSpineRender(this._skeleton, this._templet, this, this._state);
        let skinIndex = this._templet.getSkinIndexByName(this._skinName);
        if (skinIndex != -1)
            this.showSkinByIndex(skinIndex);
        this._state.addListener({
            start: (entry) => {
            },
            interrupt: (entry) => {
            },
            end: (entry) => {
            },
            dispose: (entry) => {
            },
            complete: (entry) => {
                this.event(Event.END);
                if (entry.loop) {
                    this.event(Event.COMPLETE);
                }
                else {
                    this.stop();
                }
            },
            event: (entry, event) => {
                let eventData = {
                    audioValue: event.data.audioPath,
                    audioPath: event.data.audioPath,
                    floatValue: event.floatValue,
                    intValue: event.intValue,
                    name: event.data.name,
                    stringValue: event.stringValue,
                    time: event.time * 1000,
                    balance: event.balance,
                    volume: event.volume
                };
                this.event(Event.LABEL, eventData);
                if (this._playAudio && eventData.audioValue) {
                    let channel = SoundManager.playSound(templet.basePath + eventData.audioValue, 1, Handler.create(this, this._onAniSoundStoped), null, (this._currentPlayTime * 1000 - eventData.time) / 1000);
                    SoundManager.playbackRate = this._playbackRate;
                    channel && this._soundChannelArr.push(channel);
                }
            },
        });
        this._flushExtSkin();
        this.event(Event.READY);
        if (LayaEnv.isPlaying && this._animationName !== undefined) {
            this.play(this._animationName, this._loop, true);
        }
    }
    play(nameOrIndex, loop, force = true, start = 0, end = 0, freshSkin = true, playAudio = true) {
        this._playAudio = playAudio;
        start /= 1000;
        end /= 1000;
        this._loop = loop;
        if (start < 0 || end < 0)
            throw new Error("SpineSkeleton: start and end must large than zero.");
        if ((end !== 0) && (start > end))
            throw new Error("SpineSkeleton: start must less than end.");
        if (typeof nameOrIndex == "number") {
            nameOrIndex = this.getAniNameByIndex(nameOrIndex);
        }
        else {
            let hasAni = !!this.templet.findAnimation(nameOrIndex);
            if (!hasAni)
                return;
        }
        if (force || this._pause || this._currentPlayTime || this._animationName != nameOrIndex) {
            this._animationName = nameOrIndex;
            this.spineItem.play(nameOrIndex);
            let trackEntry = this._state.setAnimation(this.trackIndex, nameOrIndex, loop);
            trackEntry.animationStart = start;
            if (!!end && end < trackEntry.animationEnd)
                trackEntry.animationEnd = end;
            let animationDuration = trackEntry.animation.duration;
            this._duration = animationDuration;
            this._playStart = start;
            this._playEnd = end <= animationDuration ? end : animationDuration;
            if (this._pause) {
                this._pause = false;
                this._beginUpdate();
            }
            this._update();
            this.event(Event.PLAYED);
        }
    }
    _update() {
        this._timeKeeper.update();
        let state = this._state;
        let delta = this._timeKeeper.delta * this._playbackRate;
        state.update(delta);
        let currentPlayTime = this._currentPlayTime = state.getCurrentPlayTime(this.trackIndex);
        state.apply(this._skeleton);
        if (!this._state || !this._skeleton) {
            return;
        }
        this._skeleton.update && this._skeleton.update(delta);
        this._skeleton.updateWorldTransform(this.physicsUpdate);
        this.spineItem.render(currentPlayTime);
        this.owner.repaint();
    }
    _flushExtSkin() {
        if (null == this._skeleton)
            return;
        let skins = this._externalSkins;
        if (skins) {
            let normal = false;
            for (let i = skins.length - 1; i >= 0; i--) {
                skins[i].flush();
            }
            this.useFastRender = false;
        }
    }
    getAnimNum() {
        return this._templet.skeletonData.getAnimationsSize();
    }
    getAniNameByIndex(index) {
        return this._templet.getAniNameByIndex(index);
    }
    getSlotByName(slotName) {
        return this._skeleton.findSlot(slotName);
    }
    playbackRate(value) {
        this._playbackRate = value;
    }
    showSkinByName(name) {
        this.showSkinByIndex(this._templet.getSkinIndexByName(name));
    }
    showSkinByIndex(skinIndex) {
        this.spineItem.setSkinIndex(skinIndex);
        this._skeleton.showSkinByIndex(skinIndex);
        this._skeleton.setSlotsToSetupPose();
    }
    event(type, data) {
        this.owner.event(type, data);
    }
    stop() {
        if (!this._pause) {
            this._pause = true;
            this._clearUpdate();
            this._state.update(-this._currentPlayTime);
            this._currentPlayTime = 0;
            this.event(Event.STOPPED);
            if (this._soundChannelArr.length > 0) {
                this._onAniSoundStoped(true);
            }
        }
    }
    _clearUpdate() {
        this._needUpdate = false;
    }
    _beginUpdate() {
        this._needUpdate = true;
    }
    onUpdate() {
        this._needUpdate && this._update();
    }
    paused() {
        if (!this._pause) {
            this._pause = true;
            this._clearUpdate();
            this.event(Event.PAUSED);
            if (this._soundChannelArr.length > 0) {
                for (let len = this._soundChannelArr.length, i = 0; i < len; i++) {
                    let channel = this._soundChannelArr[i];
                    if (!channel.isStopped) {
                        channel.pause();
                    }
                }
            }
        }
    }
    resume() {
        if (this._pause) {
            this._pause = false;
            this._beginUpdate();
            if (this._soundChannelArr.length > 0) {
                for (let len = this._soundChannelArr.length, i = 0; i < len; i++) {
                    let channel = this._soundChannelArr[i];
                    if (channel.audioBuffer) {
                        channel.resume();
                    }
                }
            }
        }
    }
    _onAniSoundStoped(force) {
        for (let len = this._soundChannelArr.length, i = 0; i < len; i++) {
            let channel = this._soundChannelArr[i];
            if (channel.isStopped || force) {
                !channel.isStopped && channel.stop();
                this._soundChannelArr.splice(i, 1);
                len--;
                i--;
            }
        }
    }
    reset() {
        this._templet._removeReference(1);
        this._templet = null;
        this._timeKeeper = null;
        this._skeleton = null;
        this._state.clearListeners();
        this._state = null;
        this._pause = true;
        this._clearUpdate();
        if (this._soundChannelArr.length > 0)
            this._onAniSoundStoped(true);
    }
    addAnimation(nameOrIndex, loop = false, delay = 0) {
        delay /= 1000;
        let animationName = nameOrIndex;
        if (typeof animationName == "number") {
            animationName = this.getAniNameByIndex(animationName);
        }
        this._animationName = animationName;
        this._state.addAnimation(this.trackIndex, animationName, loop, delay);
    }
    setMix(fromNameOrIndex, toNameOrIndex, duration) {
        duration /= 1000;
        let fromName = fromNameOrIndex;
        if (typeof fromName == "number") {
            fromName = this.getAniNameByIndex(fromName);
        }
        let toName = toNameOrIndex;
        if (typeof toName == "number") {
            toName = this.getAniNameByIndex(toName);
        }
        this._stateData.setMix(fromName, toName, duration);
    }
    getBoneByName(boneName) {
        return this._skeleton.findBone(boneName);
    }
    getSkeleton() {
        return this._skeleton;
    }
    physicsTranslate(x, y) {
        this._templet.hasPhysics && this._skeleton.physicsTranslate(x, y);
    }
    onTransformChanged() {
        if (this._skeleton) {
            let trans = this.owner.globalTrans;
            this._skeleton.x = trans.x;
            this._skeleton.y = trans.y;
        }
    }
    setSlotAttachment(slotName, attachmentName) {
        this.useFastRender = false;
        this._skeleton.setAttachment(slotName, attachmentName);
    }
    clear() {
        this._mesh = null;
        this._renderElements.forEach(element => {
            Spine2DRenderNode.recoverRenderElement2D(element);
        });
        super.clear();
    }
    changeFast() {
        if (!(this.spineItem instanceof SpineOptimizeRender)) {
            this.spineItem.destroy();
            let before = SketonOptimise.normalRenderSwitch;
            SketonOptimise.normalRenderSwitch = false;
            this.spineItem = this._templet.sketonOptimise._initSpineRender(this._skeleton, this._templet, this, this._state);
            this.spineItem.setSkinIndex(this._templet.getSkinIndexByName(this._skinName));
            SketonOptimise.normalRenderSwitch = before;
        }
    }
    changeNormal() {
        if (!(this.spineItem instanceof SpineNormalRender)) {
            this.spineItem.destroy();
            let before = SketonOptimise.normalRenderSwitch;
            SketonOptimise.normalRenderSwitch = true;
            this.spineItem = this._templet.sketonOptimise._initSpineRender(this._skeleton, this._templet, this, this._state);
            this.spineItem.setSkinIndex(this._templet.getSkinIndexByName(this._skinName));
            SketonOptimise.normalRenderSwitch = before;
        }
    }
    onDestroy() {
        if (this._templet) {
            this.reset();
        }
        this.spineItem.destroy();
    }
    _updateMaterials(elements) {
        for (let i = 0, len = elements.length; i < len; i++) {
            this._materials[i] = elements[i];
        }
    }
    _updateRenderElements() {
        let elementLength = this._renderElements.length;
        for (let i = 0; i < elementLength; i++) {
            let element = this._renderElements[i];
            let material = this._materials[i];
            element.materialShaderData = material.shaderData;
            element.subShader = material._shader.getSubShaderAt(0);
            element.value2DShaderData = this._spriteShaderData;
        }
    }
    _onMeshChange(mesh, force = false) {
        let hasChange = false;
        if (this._mesh != mesh || force) {
            hasChange = true;
            if (mesh) {
                let subMeshes = mesh._subMeshes;
                let elementLength = this._renderElements.length;
                let flength = Math.max(elementLength, mesh.subMeshCount);
                for (let i = 0; i < flength; i++) {
                    let element = this._renderElements[i];
                    let subMesh = subMeshes[i];
                    if (subMesh) {
                        if (!element) {
                            element = Spine2DRenderNode.createRenderElement2D();
                            this._renderElements[i] = element;
                        }
                        let material = this._materials[i];
                        element.geometry = subMesh;
                        element.materialShaderData = material.shaderData;
                        element.subShader = material._shader.getSubShaderAt(0);
                        element.value2DShaderData = this._spriteShaderData;
                        element.nodeCommonMap = this._getcommonUniformMap();
                    }
                    else {
                        Spine2DRenderNode.recoverRenderElement2D(element);
                    }
                }
                this._renderElements.length = mesh.subMeshCount;
                SpineShaderInit.changeVertexDefine(this._spriteShaderData, mesh);
            }
            else {
                for (let i = 0, len = this._renderElements.length; i < len; i++)
                    Spine2DRenderNode.recoverRenderElement2D(this._renderElements[i]);
                this._renderElements.length = 0;
            }
        }
        this._mesh = mesh;
        return hasChange;
    }
}
Spine2DRenderNode._pool = [];
Spine2DRenderNode.STOPPED = 0;
Spine2DRenderNode.PAUSED = 1;
Spine2DRenderNode.PLAYING = 2;
class TimeKeeper {
    constructor(timer) {
        this.maxDelta = 0.064;
        this.timer = timer;
    }
    update() {
        this.delta = this.timer.delta / 1000;
        if (this.delta > this.maxDelta)
            this.delta = this.maxDelta;
    }
}
ClassUtils.regClass("Spine2DRenderNode", Spine2DRenderNode);
